import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Button, type ButtonProps } from "~/common/mui";
import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import { isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import { Trans } from "react-i18next";
import { userIsCustomer, userIsSalesman } from "~/domains/user/utils/roles";
import { SrpButtons } from "~/features/search/components/ProductResults/views/ListView/components/SearchResultButtons";
import { EwellixRequestAQuoteButton } from "~/features/Ewellix/components/EwellixRequestAQuoteButton";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import DescriptionIcon from "@mui/icons-material/Description";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

type ThemeProps = Pick<ButtonProps, "color" | "variant">;
type ExternalButtonProps = ButtonProps & {
  forceChangeToQuote?: boolean;
};

const EWELLIX_BUTTONS = [
  {
    visible: (product: ProductDescriptor) => !isProductPMA(product) && canSeeAddToCartButton(true),
    propsKey: "atc",
    render: renderATCButton,
  },
  {
    visible: (product: ProductDescriptor) => {
      return isProductPMA(product);
    },
    propsKey: "requestAQuote",
    render: (product: ProductDescriptor, theme: ThemeProps, externalButtonProps: ExternalButtonProps) => (
      <EwellixRequestAQuoteButton key={"requestAQuote"} product={product.base} {...theme} {...externalButtonProps} />
    ),
  },
];
const SCHAEFFLER_BUTTONS = [
  {
    visible: (product: ProductDescriptor) => canSeeAddToCartButton(true),
    propsKey: "atc",
    render: renderATCButton,
  },
  {
    visible: (product: ProductDescriptor) => canSeeWishlistButton(),
    propsKey: "wishlist",
    render: renderWishlistButton,
  },
];

type PropsForButtons = {
  atc?: ExternalButtonProps;
  wishlist?: ExternalButtonProps & { sizeVariant?: "default" | "side" };
  configurator?: ExternalButtonProps;
  requestAQuote?: ExternalButtonProps;
};

function getButtonTheme(index: number): ThemeProps {
  if (index === 0) {
    return { color: "primary" };
  } else if (index === 1) {
    return { color: "secondary" };
  } else {
    return { variant: "text" };
  }
}

export function renderButtons(product: ProductDescriptor, externalProps?: PropsForButtons) {
  const buttons = product.base.externalBrand ? EWELLIX_BUTTONS : SCHAEFFLER_BUTTONS;
  return buttons
    .filter(({ visible }) => visible(product))
    .map(({ render, propsKey }, index) => {
      const key = propsKey as keyof PropsForButtons;
      const props: ExternalButtonProps = externalProps?.[key] ?? {};
      return render(product, getButtonTheme(index), props);
    });
}

function renderATCButton(
  product: ProductDescriptor,
  theme: ThemeProps,
  { forceChangeToQuote, ...externalButtonProps }: ExternalButtonProps,
) {
  let cartButtonLabel;
  let cartButtonIcon;
  let testId;
  if (userIsSalesman()) {
    testId = "pdp-add-to-quote";
    cartButtonLabel = "basket.addToQuote";
    cartButtonIcon = <DescriptionIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />;
  } else if ((userIsCustomer() && isProductPMA(product)) || forceChangeToQuote) {
    testId = "pdp-add-to-cart";
    cartButtonLabel = "cartPage.btn.quote";
    cartButtonIcon = <DescriptionIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />;
  } else {
    testId = "pdp-add-to-cart";
    cartButtonLabel = "addToCartPopup.add.submitBtn";
    cartButtonIcon = <ShoppingCartIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />;
  }

  return (
    <Button
      key={"buy"}
      startIcon={cartButtonIcon}
      fullWidth={true}
      data-test-id={testId}
      {...theme}
      {...externalButtonProps}
    >
      <Trans i18nKey={cartButtonLabel} />
    </Button>
  );
}
function renderWishlistButton(
  product: ProductDescriptor,
  theme: ThemeProps,
  { sizeVariant, ...externalButtonProps }: ExternalButtonProps & { sizeVariant?: "default" | "side" },
) {
  if (sizeVariant === "side") {
    return SrpButtons.AddToWishlistSmall({ onClick: externalButtonProps.onClick });
  }

  return (
    <Button
      key={"wishlist"}
      startIcon={<BookmarkIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
      fullWidth={true}
      data-test-id={"btn-add-to-wishlist"}
      {...theme}
      {...externalButtonProps}
    >
      <Trans i18nKey={"basket.addToWishlist"} />
    </Button>
  );
}

function productIsLinear(product: ProductDescriptor) {
  return !!product.base.configuratorUrl;
}
